// zoneResolve.js
// ─────────────────────────────────────────────────────────────────────────
// Resolve the IANA time zone for a clock point (day start or day end) from its
// location — fully offline. Geocoding gives coordinates; tz-lookup maps
// coordinates → IANA zone with no network call, no API key, no proxy.
//
// Only two clock points exist per day (start, stop), so this runs at most twice
// per day. A day "crossed" a zone iff startZone !== endZone.
//
// Requires: tz-lookup (npm i tz-lookup)
// Reuses:   geocodeCached from googleMapsTravel.js (already caches lat/lng)
// ─────────────────────────────────────────────────────────────────────────

import tzLookup from 'tz-lookup';
import { geocodeCached } from './googleMapsTravel.js';

/**
 * Resolve an IANA zone for a location.
 *
 * @param {string|{lat:number,lng:number}} location  address string OR coords
 * @returns {Promise<string|null>} IANA zone (e.g. 'America/Dawson_Creek'),
 *   or null if the location can't be geocoded (bare 'Hotel', empty, etc.) —
 *   caller falls back to nearest-stop zone or the manual dropdown.
 */
export const zoneForLocation = async (location) => {
  let lat, lng;
  if (location && typeof location === 'object' && location.lat != null) {
    ({ lat, lng } = location);
  } else if (typeof location === 'string' && location.trim()) {
    const coords = await geocodeCached(location);
    if (!coords) return null;
    ({ lat, lng } = coords);
  } else {
    return null;
  }
  try {
    return tzLookup(lat, lng); // synchronous, offline
  } catch {
    return null; // out-of-range coords, etc.
  }
};

/**
 * Resolve start + end zones for a day and report whether it crossed.
 * Pass the same start-origin / end-destination the travel calc uses
 * (home base or hotel, honouring dayOverrides).
 *
 * @returns {Promise<{startZone:string|null, endZone:string|null, crossed:boolean}>}
 */
export const resolveDayZones = async (startLocation, endLocation) => {
  const [startZone, endZone] = await Promise.all([
    zoneForLocation(startLocation),
    zoneForLocation(endLocation),
  ]);
  // crossed only when both are known and differ; unknown zones never assert a crossing
  const crossed = !!startZone && !!endZone && startZone !== endZone;
  return { startZone, endZone, crossed };
};
